import {
  ChevronRightIcon,
  MenuListItemVariant,
  SwipeableListItemType,
  WfIconContentDraggableRegular,
} from "@WahooFitness/redesignr";
import { useNavigate, useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import { ChangeEvent, MouseEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useWorkoutProfilesContext } from "@/hooks/useWorkoutProfilesContext";
import Switch from "@mui/material/Switch";
import useWorkoutPageDisplayStrings from "./useWorkoutPageDisplayStrings";
import { useUserContext, hasMinProfile } from "@WahooFitness/wahoo-offline-mfe";
import { getCruxAppProfile } from "@/services/AppProfileUtil";
import { Typography } from "@mui/material";
import {
  BoltDisplayConfig,
  BoltDisplayPage,
  crux_bolt_display_page_type_e,
} from "@WahooFitness/crux-js-display-cfg";
import useAssociatedDevices from "@/hooks/useAssociatedDevices";
import useFlaggedFeatures from "@/hooks/useFlaggedFeatures";
import { FlaggedFeature } from "@/hooks/types/FlaggedFeature";
import { useSettingsVersionedFeatures } from "@/hooks/useSettingsVersionedFeatures";
import { Profile, WahooWorkoutTypeLocation } from "@WahooFitness/cloud-client-types";
import { useCruxJsDisplayConfig } from "@/hooks/useCruxJsDisplayConfig";
import { arrayMove } from "@dnd-kit/sortable";
import { useDragAndDrop } from "../../../hooks/useDragAndDrop";
import { t } from "@lingui/macro";
import { useLogEvent } from "@/services/Analytics/useLogEvent";
import { EventType, SourcePage } from "@/services/Analytics/EventTypes";

export type WorkoutPage = {
  name: string;
  description: string;
  toggleable: boolean;
  editable: boolean;
  enabled: boolean;
  deleteable: boolean;
  id: number;
  hasSubPage: boolean;
  showElemntTag: boolean;
  showAceTag: boolean;
};

const ACE_ONLY_PAGES = [crux_bolt_display_page_type_e.BOLTAPP_WIND_SPEED];
const ELEMNT_ONLY_PAGES = [
  crux_bolt_display_page_type_e.BOLTAPP_ELEVATION,
  crux_bolt_display_page_type_e.BOLTAPP_CLIMB,
  crux_bolt_display_page_type_e.BOLTAPP_PEDAL_MONITOR,
  crux_bolt_display_page_type_e.BOLTAPP_SEGMENT,
  crux_bolt_display_page_type_e.BOLTAPP_MULTISPORT,
  crux_bolt_display_page_type_e.BOLTAPP_GOPRO,
  crux_bolt_display_page_type_e.BOLTAPP_MUSIC,
  crux_bolt_display_page_type_e.BOLTAPP_WAYPOINTS,
  crux_bolt_display_page_type_e.BOLTAPP_LAP_SPLITS,
];

const useWorkoutPages = (elevation?: boolean) => {
  const { profileId } = useParams();
  const {
    getWorkoutProfile,
    getWorkoutProfilePages,
    updateWorkoutProfilePagesDebounce,
    updateWorkoutProfilePagesImmediately,
    workoutProfilesError,
  } = useWorkoutProfilesContext();

  const { checkIsFeatureEnabled } = useFlaggedFeatures();

  const { enabled: gen4ReleaseEnabled, featuresAreLoading: gen4ReleaseFlagLoading } =
    useSettingsVersionedFeatures("ELEMNT_GEN4_RELEASE_MINPROFILE_VERSION");

  const { logEvent } = useLogEvent();

  const { workoutPageDescriptions, workoutPageTranslations } = useWorkoutPageDisplayStrings();

  const workoutProfile = useMemo(
    () => (profileId ? getWorkoutProfile(profileId) : undefined),
    [getWorkoutProfile, profileId]
  );

  const { currentUser, userIsLoading } = useUserContext();

  const { hasAssociatedAce, hasAssociatedElemnt } = useAssociatedDevices();

  const checkPageEnabled = useCallback(
    (page: BoltDisplayPage) => {
      if (
        !workoutPageTranslations[page.getType()] &&
        !hasMinProfile(Profile.alpha, currentUser?.app?.profile)
      ) {
        return false;
      }
      switch (page.getType()) {
        case crux_bolt_display_page_type_e.BOLTAPP_MULTISPORT:
          return checkIsFeatureEnabled(FlaggedFeature.ElemntMultisportPage);
        case crux_bolt_display_page_type_e.BOLTAPP_SEGMENT:
          return workoutProfile?.workout_type_location_id !== WahooWorkoutTypeLocation.Indoor;
        case crux_bolt_display_page_type_e.BOLTAPP_WIND_SPEED:
          return hasAssociatedAce;
        case crux_bolt_display_page_type_e.BOLTAPP_LAP_SPLITS:
          return gen4ReleaseEnabled;
        default:
          return true;
      }
    },
    [
      checkIsFeatureEnabled,
      currentUser?.app?.profile,
      gen4ReleaseEnabled,
      hasAssociatedAce,
      workoutPageTranslations,
      workoutProfile?.workout_type_location_id,
    ]
  );

  const flushPendingSave = useCallback(
    () => updateWorkoutProfilePagesDebounce.flush(),
    [updateWorkoutProfilePagesDebounce]
  );

  useEffect(() => {
    // This ensures that any pending save actions are executed when the component is unmounted
    return () => {
      flushPendingSave();
    };
  }, [flushPendingSave, updateWorkoutProfilePagesDebounce]);

  const [displayConfig, setDisplayConfig] = useState<BoltDisplayConfig | undefined>(undefined);

  const appProfile = useMemo(() => currentUser?.app?.profile, [currentUser]);

  const { cruxJsDisplayConfig } = useCruxJsDisplayConfig();

  useEffect(() => {
    if (userIsLoading || !cruxJsDisplayConfig) return;
    (async () => {
      if (!workoutProfile) return;
      let savedWorkoutPages = undefined;
      const savedWorkoutPagesData = await getWorkoutProfilePages(workoutProfile.external_id);
      if (savedWorkoutPagesData) {
        savedWorkoutPages =
          cruxJsDisplayConfig.getDisplayConfigFromUint8Array(savedWorkoutPagesData);
      }

      const displayConfig =
        savedWorkoutPages ||
        cruxJsDisplayConfig.getDisplayConfig(
          workoutProfile.workout_type_id?.valueOf() ?? 0,
          getCruxAppProfile(appProfile)
        );
      if (!displayConfig) {
        return;
      }
      setDisplayConfig(displayConfig);
    })();
  }, [appProfile, cruxJsDisplayConfig, getWorkoutProfilePages, userIsLoading, workoutProfile]);

  const [pagesAreLoading, setPagesAreLoading] = useState(true);

  const elevationPage = useMemo(() => {
    if (!displayConfig) return undefined;
    for (let i = 0; i < displayConfig.getPageCount(); i++) {
      const page = displayConfig.getPageAt(i);
      if (page.getType() === crux_bolt_display_page_type_e.BOLTAPP_ELEVATION) {
        return page;
      }
    }
    return undefined;
  }, [displayConfig]);

  const boltDisplayPages = useMemo(() => {
    if (!displayConfig) return [];
    if (elevation) {
      const climbSubPage = elevationPage?.getClimbSubPage();
      setPagesAreLoading(false);
      const pages: BoltDisplayPage[] = [];
      if (elevationPage) {
        pages.push(elevationPage);
      }
      if (climbSubPage) {
        pages.push(climbSubPage);
      }
      return pages;
    }
    const pageCount = displayConfig.getPageCount();
    setPagesAreLoading(false);

    return new Array(pageCount)
      .fill(0)
      .map((_, i) => displayConfig.getPageAt(i))
      .filter((p) => checkPageEnabled(p))
      .filter((page) => {
        if (hasAssociatedElemnt) return true;
        return !ELEMNT_ONLY_PAGES.includes(page.getType());
      });
  }, [checkPageEnabled, displayConfig, elevation, elevationPage, hasAssociatedElemnt]);

  const [pages, setPages] = useState<Array<WorkoutPage>>([]);

  const isIndoorProfile = useMemo(() => {
    return workoutProfile?.workout_type_location_id === WahooWorkoutTypeLocation.Indoor;
  }, [workoutProfile?.workout_type_location_id]);

  useEffect(() => {
    setPages(
      boltDisplayPages.map((page) => {
        const pageNameOverride =
          page.getType() === crux_bolt_display_page_type_e.BOLTAPP_MAP && isIndoorProfile
            ? t`Route`
            : undefined;
        return {
          name:
            page.getCustomTitle() ||
            pageNameOverride ||
            workoutPageTranslations[page.getType()] ||
            `${page.getName()} (alpha only)`,
          description: workoutPageDescriptions[page.getType()],
          toggleable: page.canDisable(),
          editable: page.canEdit(),
          enabled: page.isEnabled(),
          deleteable: page.canDelete(),
          id: page.getId(),
          hasSubPage: !!page.getClimbSubPage(),
          showElemntTag: ELEMNT_ONLY_PAGES.includes(page.getType()),
          showAceTag: ACE_ONLY_PAGES.includes(page.getType()),
        };
      })
    );
  }, [
    isIndoorProfile,
    boltDisplayPages,
    workoutPageDescriptions,
    workoutPageTranslations,
    hasAssociatedElemnt,
  ]);

  const savePages = useCallback(
    async (immediately?: boolean, encodedDisplayConfig?: Uint8Array) => {
      if (workoutProfile && displayConfig) {
        const saveFunction = immediately
          ? updateWorkoutProfilePagesImmediately
          : updateWorkoutProfilePagesDebounce;
        await saveFunction(
          workoutProfile.external_id,
          encodedDisplayConfig ?? displayConfig.getEncoded()
        );
      }
    },
    [
      displayConfig,
      updateWorkoutProfilePagesDebounce,
      updateWorkoutProfilePagesImmediately,
      workoutProfile,
    ]
  );

  const togglePage = useCallback(
    (pageIndex: number, newValue: boolean) => {
      const boltDisplayPage = boltDisplayPages[pageIndex];
      boltDisplayPage.setEnabled(newValue);
      if (boltDisplayPage.getType() === crux_bolt_display_page_type_e.BOLTAPP_LAP_SPLITS) {
        logEvent("lap_page_enabled", {
          event_type: EventType.click,
          source_page_name: SourcePage.workout_profile_pages,
          enabled: newValue,
          workout_profile_id: profileId,
        });
      }
      const updatePage = boltDisplayPage.updateParentPage();
      displayConfig?.update(updatePage);
      savePages();
      setPages((oldPages) => {
        const page = oldPages[pageIndex];
        const newPages = [...oldPages];
        newPages[pageIndex] = { ...page, enabled: newValue };
        return newPages;
      });
    },
    [boltDisplayPages, displayConfig, logEvent, profileId, savePages]
  );

  const handlePageSwitchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>, index: number) =>
      togglePage(index, event.target.checked),
    [togglePage]
  );

  const navigate = useNavigate();

  const addCustomPage = useCallback(
    async (pageName: string) => {
      if (!workoutProfile || !displayConfig) return;
      displayConfig.addPage(
        workoutProfile.workout_type_id?.valueOf() ?? 0,
        getCruxAppProfile(appProfile)
      );
      const newPage = displayConfig.getPageAt(displayConfig.getPageCount() - 1);
      newPage.setCustomTitle(pageName);
      const newDisplayConfig = displayConfig.update(newPage);
      setDisplayConfig(newDisplayConfig);
      await savePages(true, newDisplayConfig.getEncoded());
      navigate(`/workout-profiles/${profileId}/workout-pages/${newPage.getId()}`);
    },
    [appProfile, displayConfig, navigate, profileId, savePages, workoutProfile]
  );

  const resetPagesToDefault = useCallback(() => {
    if (!workoutProfile || !displayConfig) return;
    const newDisplayConfig = displayConfig.resetToDefault(
      workoutProfile.workout_type_id?.valueOf() ?? 0,
      getCruxAppProfile(appProfile)
    );
    setDisplayConfig(newDisplayConfig);
    savePages(true, newDisplayConfig.getEncoded());
  }, [appProfile, displayConfig, savePages, workoutProfile]);

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [pageToDelete, setPageToDelete] = useState<WorkoutPage | undefined>(undefined);

  const deletePage = useCallback(() => {
    if (!workoutProfile || !displayConfig || !pageToDelete) return;
    displayConfig.deletePageWithId(pageToDelete.id);
    savePages(true);
  }, [displayConfig, pageToDelete, savePages, workoutProfile]);

  const onDeleteClick = useCallback((page: WorkoutPage) => {
    setPageToDelete(page);
    setShowDeleteDialog(true);
  }, []);

  const onCloseDeleteDialog = useCallback(() => {
    setShowDeleteDialog(false);
    setTimeout(() => {
      setPageToDelete(undefined);
    }, 50);
  }, []);

  const goToWorkoutPageEditor = useCallback(
    (pageId: number) => {
      navigate(`/workout-profiles/${profileId}/workout-pages/${pageId}`);
    },
    [navigate, profileId]
  );

  const stopEventPropagation = useCallback((event: MouseEvent) => {
    event.stopPropagation();
  }, []);

  const handleDeleteClick = useCallback(
    (page: WorkoutPage) => {
      onDeleteClick(page);
    },
    [onDeleteClick]
  );

  const movePage = useCallback(
    (startIndex: number, newIndex: number) => {
      const pageToMove = boltDisplayPages[startIndex];
      const pageAtDestination = boltDisplayPages[newIndex];
      const pagesJson = displayConfig?.toJson().pages;
      if (!pageToMove || !pageAtDestination || !pagesJson) return;

      const pageToMoveIndex = pagesJson.findIndex((p: any) => p.page_id === pageToMove.getId());
      const pageAtDestinationIndex = pagesJson.findIndex(
        (p: any) => p.page_id === pageAtDestination.getId()
      );
      if (pageToMoveIndex === -1 || pageAtDestinationIndex === -1) return;

      displayConfig?.movePage(pageToMoveIndex, pageAtDestinationIndex);

      setPages((oldPages) => arrayMove(oldPages, startIndex, newIndex));
      savePages();
    },
    [boltDisplayPages, displayConfig, savePages]
  );

  const getPageAction = useCallback(
    (page: WorkoutPage) => {
      if (page.hasSubPage && !elevation) {
        return () => navigate(`/workout-profiles/${profileId}/workout-pages/elevation`);
      }
      if (elevation && !page.hasSubPage) {
        return () => navigate(`/workout-profiles/${profileId}/workout-pages/elevation/edit`);
      }
      return () => goToWorkoutPageEditor(page.id);
    },
    [elevation, goToWorkoutPageEditor, navigate, profileId]
  );

  const [swipedId, setSwipedId] = useState<string | null>(null);

  const { handleDragStart, handleDragEnd, sensors, activeId } = useDragAndDrop<WorkoutPage>(
    pages,
    (p) => String(p.id),
    movePage,
    setSwipedId
  );
  const pageMenuLists = useMemo(
    () =>
      pages.map(
        (page, index) =>
          ({
            key: page.id.toString(),
            pageListItems: [
              {
                id: `${page.id}-${page.name}`,
                content: (
                  <Box display="flex" flexDirection="row" alignItems="center">
                    {page.toggleable && !elevation && (
                      <Switch
                        color="info"
                        checked={page.enabled}
                        onClick={stopEventPropagation}
                        onChange={(event) => handlePageSwitchChange(event, index)}
                        sx={{ mr: 1.5 }}
                      />
                    )}
                    <Box>
                      {page.showElemntTag && (
                        <Typography variant="prose-xs-medium" color="text.disabled">
                          ELEMNT
                        </Typography>
                      )}
                      {page.showAceTag && (
                        <Typography variant="prose-xs-medium" color="text.disabled">
                          ACE
                        </Typography>
                      )}
                      <Typography>{page.name}</Typography>
                      {!elevation && !page.hasSubPage && (
                        <Typography color="text.secondary" variant="prose-sm">
                          {page.description}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ),
                variant: page.deleteable
                  ? MenuListItemVariant.Swipeable
                  : MenuListItemVariant.Action,
                actionComponent: (
                  <Box display="flex" flexDirection="row" alignItems="center">
                    <WfIconContentDraggableRegular sx={{ color: "text.disabled" }} />

                    <>
                      {page.editable ? (
                        <ChevronRightIcon sx={{ color: "text.secondary" }} />
                      ) : (
                        <Box height="24px" width="24px" />
                      )}
                    </>
                  </Box>
                ),
                action: page.editable ? getPageAction(page) : undefined,
                setOpenSwipeId: (id: string) => setSwipedId(id),
                openSwipeId: swipedId,
                onSwipeAction: () => {
                  handleDeleteClick(page);
                },
                swipeActionColor: "error",
                isSorting: activeId === String(page.id),
              },
            ],
          }) as { key: string; pageListItems: SwipeableListItemType[] }
      ),
    [
      pages,
      elevation,
      stopEventPropagation,
      swipedId,
      getPageAction,
      activeId,
      handlePageSwitchChange,
      handleDeleteClick,
    ]
  );

  const getPage = useCallback(
    (pageId: number) => {
      return boltDisplayPages.find((page) => page.getId() === pageId);
    },
    [boltDisplayPages]
  );

  const getCategoriesForPageType = useCallback(
    (pageType?: number) => {
      if (
        pageType === undefined ||
        workoutProfile?.workout_type_id === undefined ||
        !displayConfig
      ) {
        return [];
      }
      return displayConfig.getCategories(
        workoutProfile.workout_type_id,
        pageType,
        getCruxAppProfile(appProfile)
      );
    },
    [appProfile, displayConfig, workoutProfile]
  );

  const maxPageNameLength = 25;

  return {
    pagesAreLoading: pagesAreLoading || gen4ReleaseFlagLoading,
    pagesError: workoutProfilesError,
    pageMenuLists,
    togglePage,
    savePages,
    flushPendingSave,
    getPage,
    getCategoriesForPageType,
    displayConfig,
    setDisplayConfig,
    addCustomPage,
    resetPagesToDefault,
    showDeleteDialog,
    onCloseDeleteDialog,
    deletePage,
    movePage,
    elevationPage,
    maxPageNameLength,
    setSwipedId,
    pageToDelete,
    setPageToDelete,
    sensors,
    handleDragStart,
    handleDragEnd,
    activeId,
  };
};

export default useWorkoutPages;
